import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import { ReportSchedulingEmails } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { RangeCombo, Validation, ValidationResponse } from '../../Common/Components/Selects/RangeCombo/RangeCombo'
import FormRow from '../../Common/Components/FormRow'
import Heading, { HeadingLevel } from '../../Common/Components/Heading'
import Fieldset from '../../Common/Components/Fieldset'
import { UserInfo, useUserInfo } from '../../../ReactRouting/Hooks/useUserInfo'
import { isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { SelectOptions } from '../../Common/Components/Selects'
import { PermissionsUtil } from '../../Common/Utils/PermissionsUtil'
import { getTelSysInternal, getUsersByTelSysAndPartition } from '../../../NonReact/common/API/EntityAPIClient'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { APIListData, JSONResponse, User } from '../../../NonReact/common/Types/Types'
import { EmailUtils } from '../../Common/Utils/EmailUtils'
import { displayError } from '../../Common/Utils/Messages'
import { SelectOption } from '../../Common/Components/Selects/Types'
import { EMAIL_ADDR } from '../../../NonReact/constants/UserConstants'
import { TelSysAdminDTO, TelSysModalConfigDTO } from '../../../NonReact/Types/Types'
import { DEFAULT_ALLOWED_CUSTOM_EMAILS } from './Constants'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'

type Props = {
    selectedEmailsToSend: string
    setSelectedEmailsToSend: (value: string) => void
    telSysModalConfig?: Nullable<TelSysModalConfigDTO>
    setHasValidationWarnings: (value: boolean) => void
}

const ScheduledEmail: FC<Props> = ({
    selectedEmailsToSend,
    setSelectedEmailsToSend,
    telSysModalConfig,
    setHasValidationWarnings
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(ReportSchedulingEmails.NAME)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [userInfo]: UserInfo = useUserInfo()

    const [availableEmails, setAvailableEmails]: ReactState<SelectOptions<string>> = useState<SelectOptions<string>>([])
    const [userTelSys, setSelectedTelSys]: ReactState<JSONResponse<TelSysAdminDTO>> =
        useState<JSONResponse<TelSysAdminDTO>>(null)

    const updateAvailableEmails = useCallback(
        (emails: string[]): void => {
            const domain: string = EmailUtils.getDomain(userInfo?.EmailAddr ?? EMPTY_STRING).toLowerCase()

            if (isStringNotEmpty(domain)) {
                const filteredEmails: SelectOptions<string> = emails
                    .filter(
                        (email: string): boolean =>
                            EmailUtils.getDomainLower(email) === domain && userInfo?.EmailAddr !== email
                    )
                    .map(
                        (entry: string): SelectOption<string> =>
                            ({
                                value: entry,
                                label: entry
                            }) as SelectOption<string>
                    )
                setAvailableEmails(filteredEmails)

                return undefined
            }

            setAvailableEmails([])

            return undefined
        },
        [userInfo?.EmailAddr]
    )

    useEffect((): void => {
        if (IsVarEmpty(userInfo) || PermissionsUtil.isCMEWideUser(userInfo)) return

        const telSysId: number = userInfo?.Scope?.TelSysID?.[0]
        const partId: number = userInfo?.Scope?.PartIDInTelSys?.[0] ?? ALL_VALUE

        getUsersByTelSysAndPartition(telSysId, partId, [EMAIL_ADDR])
            .then((users: JSONResponse<APIListData<User>>): void => {
                if (isVarNotEmpty(users) && isMounted.current) {
                    updateAvailableEmails(
                        users.Data?.Items.map((user: User): string => user?.EmailAddr ?? EMPTY_STRING) ?? []
                    )
                }
            })
            .catch(displayError)

        getTelSysInternal(telSysId)
            .then((telSys: JSONResponse<TelSysAdminDTO>): void => {
                if (isMounted.current) {
                    setSelectedTelSys(telSys)
                }
            })
            .catch(displayError)
    }, [userInfo])

    const availableSet: Set<string> = new Set(availableEmails.map((email: SelectOption<string>): string => email.value))

    const validations: Validation = {
        // checks if new email is a valid email
        validDomainValue: (newValue: string): ValidationResponse => ({
            success: isStringNotEmpty(newValue) ? EmailUtils.isValid(newValue) : true,
            message: language.get(ReportSchedulingEmails.Validation_Valid_Domain_Name)
        }),
        // Checks if the email is in same domain as user
        sameDomainCheck: (newValue: string): ValidationResponse => ({
            success:
                isStringNotEmpty(newValue) && isVarNotEmpty(userInfo?.EmailAddr)
                    ? EmailUtils.areSameDomain(userInfo?.EmailAddr ?? EMPTY_STRING, newValue)
                    : true,
            message: language.get(ReportSchedulingEmails.Validation_Valid_Same_As_User)
        }),
        // Check if new email is a blacklisted one.
        checkBlacklistedEmail: (newValue: string): ValidationResponse => ({
            success: isVarNotEmpty(telSysModalConfig?.ReportEmailDomainBlacklist)
                ? EmailUtils.shouldDisplayByBlacklist(telSysModalConfig?.ReportEmailDomainBlacklist, newValue)
                : true,
            message: language.get(ReportSchedulingEmails.Validation_Valid_Non_Blacklisted_Email)
        }),
        // Check if custom domain exceeds the default. Custom means a user that's not a user in Akixi
        checkCustomEmails: (_: string, allValues: Nullable<string[]>): ValidationResponse => {
            if (IsVarEmpty(availableEmails) || IsVarEmpty(telSysModalConfig)) {
                return {
                    success: true,
                    message: EMPTY_STRING
                }
            }

            const customEmails: Definable<string[]> = allValues?.filter(
                (entry: string): boolean => !availableSet.has(entry)
            )

            const defaultAllowedCustomEmails: number =
                telSysModalConfig?.CustomEmailsPerScheduledReport ?? DEFAULT_ALLOWED_CUSTOM_EMAILS

            const cantAddCustomDomain: boolean =
                isVarNotEmpty(customEmails) && customEmails.length > defaultAllowedCustomEmails

            return {
                success: !cantAddCustomDomain,
                message: language.getInterpolated(
                    ReportSchedulingEmails.Validation_Valid_Max_Custom_Domains,
                    defaultAllowedCustomEmails
                )
            }
        }
    }

    return (
        <Fieldset className='scheduled-email-fieldset'>
            <Heading level={HeadingLevel.ONE}>{language.get(ReportSchedulingEmails.Section_Header)}</Heading>
            <FormRow className='schedule-emails'>
                <div className='se-range-combo'>
                    <RangeCombo
                        label={language.get(ReportSchedulingEmails.Section_SubHeader)}
                        options={availableEmails}
                        originalValue={selectedEmailsToSend}
                        onChange={(value?: string): void => setSelectedEmailsToSend(value ?? EMPTY_STRING)}
                        maxEntries={Number(userTelSys?.Advanced?.EmailsPerScheduledReport ?? 0)}
                        validation={validations}
                        disableExcluding={true}
                        setHasValidationWarnings={setHasValidationWarnings}
                        onCustomEmailCheck={(email: string): boolean => !availableSet.has(email)}
                    />
                </div>
                <HelpTag helpContentTitle={HelpTagTitles.SCHEDULED_EMAILS_RECIPIENTS} tag={Tags.CONFIGURE_REPORT} />
            </FormRow>
        </Fieldset>
    )
}

export default ScheduledEmail
